export const TIP_PERCENT_OPTION_IDS = ['tip-p1', 'tip-p2', 'tip-p3'] as const
export const TIP_NONE_OPTION_ID = 'tip-none'
export const TIP_CUSTOM_OPTION_ID = 'tip-custom'

export const TIP_PERCENT_MIN = 0
export const TIP_PERCENT_MAX = 100

export const LEGACY_TIP_CONTENT_KEYS = [
  'percent1',
  'label1',
  'percent2',
  'label2',
  'percent3',
  'label3',
  'defaultOptionIndex',
] as const

export type TipPercentOptionId = (typeof TIP_PERCENT_OPTION_IDS)[number]

export type TipPercentOption = {
  id: TipPercentOptionId
  percent: number
  label: string
}

export type TipSectionContent = Record<string, unknown>

export type ParsedTipConfig = {
  options: TipPercentOption[]
  defaultOptionId: string
  showNoneOption: boolean
  noneLabel: string
  showToggle: boolean
  defaultTipEnabled: boolean
}

export type TipDefaultOptionChoice = {
  id: string
  label: string
}

const DEFAULT_PERCENTS: Record<TipPercentOptionId, number> = {
  'tip-p1': 10,
  'tip-p2': 15,
  'tip-p3': 20,
}

export function defaultTipOptions(): TipPercentOption[] {
  return TIP_PERCENT_OPTION_IDS.map((id) => ({
    id,
    percent: DEFAULT_PERCENTS[id],
    label: '',
  }))
}

export function sanitizeTipPercentInput(raw: string): string {
  return String(raw).replace(/\D/g, '')
}

export function clampTipPercentString(raw: string): string {
  const digits = sanitizeTipPercentInput(raw)
  if (!digits) return ''
  const n = Number.parseInt(digits, 10)
  if (!Number.isFinite(n)) return ''
  return String(Math.min(TIP_PERCENT_MAX, Math.max(TIP_PERCENT_MIN, n)))
}

export function clampTipPercentNumber(value: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback
  return Math.min(TIP_PERCENT_MAX, Math.max(TIP_PERCENT_MIN, value))
}

export function parseTipBoolean(value: unknown, defaultEnabled = true): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value !== 'string') return defaultEnabled
  const raw = value.trim().toLowerCase()
  if (!raw) return defaultEnabled
  if (raw === 'true' || raw === '1' || raw === 'yes' || raw === 'on') return true
  if (raw === 'false' || raw === '0' || raw === 'no' || raw === 'off') return false
  return defaultEnabled
}

function parsePercent(value: string | undefined, fallback: number): number {
  const clamped = clampTipPercentString(String(value ?? ''))
  if (!clamped) return fallback
  return Number.parseInt(clamped, 10)
}

function isTipPercentOptionId(value: string): value is TipPercentOptionId {
  return (TIP_PERCENT_OPTION_IDS as readonly string[]).includes(value)
}

function normalizeTipOptionsFromArray(parsed: unknown[]): TipPercentOption[] {
  const byId = new Map<TipPercentOptionId, TipPercentOption>()
  for (const item of parsed) {
    if (!item || typeof item !== 'object') continue
    const id = String((item as TipPercentOption).id ?? '').trim()
    if (!isTipPercentOptionId(id)) continue
    byId.set(id, {
      id,
      percent: clampTipPercentNumber(Number((item as TipPercentOption).percent), DEFAULT_PERCENTS[id]),
      label: String((item as TipPercentOption).label ?? '').trim(),
    })
  }
  return TIP_PERCENT_OPTION_IDS.map((id) => byId.get(id) ?? {
    id,
    percent: DEFAULT_PERCENTS[id],
    label: '',
  })
}

export function normalizeTipOptions(options: TipPercentOption[]): TipPercentOption[] {
  return normalizeTipOptionsFromArray(options)
}

export function parseTipOptions(raw: unknown): TipPercentOption[] {
  if (Array.isArray(raw)) return normalizeTipOptionsFromArray(raw)
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw) as unknown
      if (Array.isArray(parsed)) return normalizeTipOptionsFromArray(parsed)
    } catch {
      return defaultTipOptions()
    }
  }
  return defaultTipOptions()
}

function hasTipOptions(raw: unknown): boolean {
  if (Array.isArray(raw) && raw.length > 0) return true
  return typeof raw === 'string' && raw.trim().length > 0
}

function legacyDefaultOptionId(
  content: TipSectionContent,
  options: TipPercentOption[],
): string {
  const rawIndex = Number.parseInt(String(content.defaultOptionIndex ?? '0'), 10) || 0
  if (rawIndex === 3) return TIP_NONE_OPTION_ID
  return options[rawIndex]?.id ?? options[0]?.id ?? TIP_PERCENT_OPTION_IDS[0]
}

export function migrateTipContent(content: TipSectionContent): TipSectionContent {
  const next: TipSectionContent = { ...content }
  const showNoneOption = parseTipBoolean(content.showNoneOption, true)
  const showToggle = parseTipBoolean(content.showToggle, true)
  const defaultTipEnabled = parseTipBoolean(content.defaultTipEnabled, true)

  if (!hasTipOptions(content.tipOptions)) {
    const options = TIP_PERCENT_OPTION_IDS.map((id, index) => {
      const slot = index + 1
      return {
        id,
        percent: parsePercent(String(content[`percent${slot}`] ?? ''), DEFAULT_PERCENTS[id]),
        label: String(content[`label${slot}`] ?? '').trim(),
      }
    })
    next.tipOptions = normalizeTipOptions(options)
    next.defaultOptionId = legacyDefaultOptionId(content, options)
  } else {
    next.tipOptions = parseTipOptions(content.tipOptions)
  }

  next.showToggle = showToggle
  next.defaultTipEnabled = defaultTipEnabled
  next.showNoneOption = showNoneOption

  const options = parseTipOptions(next.tipOptions)
  const allowedIds = new Set<string>(options.map((option) => option.id))
  if (showNoneOption) allowedIds.add(TIP_NONE_OPTION_ID)

  let defaultOptionId = String(next.defaultOptionId ?? '').trim()
  if (!allowedIds.has(defaultOptionId)) {
    defaultOptionId = options[0]?.id ?? TIP_PERCENT_OPTION_IDS[0]
  }
  next.defaultOptionId = defaultOptionId

  return next
}

export function parseTipConfig(content: TipSectionContent): ParsedTipConfig {
  const migrated = migrateTipContent(content)
  const options = parseTipOptions(migrated.tipOptions)
  const showNoneOption = parseTipBoolean(migrated.showNoneOption, true)
  const showToggle = parseTipBoolean(migrated.showToggle, true)
  const defaultTipEnabled = parseTipBoolean(migrated.defaultTipEnabled, true)

  const allowedIds = new Set<string>(options.map((option) => option.id))
  if (showNoneOption) allowedIds.add(TIP_NONE_OPTION_ID)

  let defaultOptionId = String(migrated.defaultOptionId ?? '').trim()
  if (!allowedIds.has(defaultOptionId)) {
    defaultOptionId = options[0]?.id ?? TIP_PERCENT_OPTION_IDS[0]
  }

  return {
    options,
    defaultOptionId,
    showNoneOption,
    noneLabel: String(content.noneLabel ?? '').trim() || 'No tip',
    showToggle,
    defaultTipEnabled,
  }
}

export function buildTipDefaultOptionChoices(config: ParsedTipConfig): TipDefaultOptionChoice[] {
  const choices: TipDefaultOptionChoice[] = config.options.map((option) => ({
    id: option.id,
    label: option.label || `${option.percent}%`,
  }))
  if (config.showNoneOption) {
    choices.push({ id: TIP_NONE_OPTION_ID, label: config.noneLabel })
  }
  return choices
}

export function calculateTipFromPercent(subtotal: number, percent: number): number {
  const base = Number(subtotal) || 0
  const p = clampTipPercentNumber(Number(percent), 0)
  return Math.round(((base * p) / 100) * 100) / 100
}
